import { IconAngleRight } from '@iwoca/icons';
import { Pill } from '@iwoca/orion';
import { clsx } from 'clsx';

import { useMarkReadOnView } from './hooks/useMarkReadOnView.hook';
import { SellerWhatsNewNotification } from '../../api/lending/lapiHooks';

const TAG_LABELS: Record<SellerWhatsNewNotification['tag'], string> = {
  product_update: 'Product update',
  feature_update: 'Feature update',
  coming_soon: 'Coming soon!',
};

// `date` is authored server-side; guard against a missing/malformed value so a
// bad date can't render the literal "Invalid Date" into the card header.
const formatDate = (isoDate: string): string | null => {
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime())
    ? null
    : parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
};

// The API doesn't expose an image. The design has an optional image, so we
// allow an optional imageUrl the card renders when present — the backend can
// add the field later with no FE change.
type WhatsNewCardNotification = SellerWhatsNewNotification & {
  imageUrl?: string;
};

// linkUrl is authored in Django admin, so treat it as untrusted: only allow
// http(s) hrefs. Anything else (e.g. `javascript:`) is dropped rather than
// rendered as a clickable link. rel="noopener" alone doesn't stop a
// javascript: scheme from executing on click.
const asSafeHref = (url: string): string | null => {
  try {
    const { protocol } = new URL(url, window.location.origin);
    return protocol === 'http:' || protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
};

export const WhatsNewCard = ({
  notification,
  onView,
  onLearnMore,
}: {
  notification: WhatsNewCardNotification;
  onView: () => void;
  onLearnMore?: () => void;
}) => {
  const {
    heading,
    subHeading,
    body,
    tag,
    date,
    linkUrl,
    linkText,
    isRead,
    imageUrl,
  } = notification;

  const cardRef = useMarkReadOnView({ isRead, onView });

  const safeHref = linkUrl ? asSafeHref(linkUrl) : null;
  const formattedDate = formatDate(date);

  return (
    <div
      ref={cardRef}
      data-testid="whatsNewCard"
      className="flex w-full flex-col gap-l border-0 border-b border-solid border-secondary-90 pb-xl last:border-b-0 last:pb-0"
    >
      {/* Media + content: stacked on mobile, image-left on desktop */}
      <div className="flex flex-col gap-l min-l:flex-row min-l:gap-xl">
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            className="aspect-[330/224] w-full shrink-0 rounded-s object-cover min-l:h-[224px] min-l:w-[330px]"
          />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-s">
          {/* Tag pill + release date */}
          <div className="flex items-center justify-between gap-s">
            <Pill variant="green" size="small">
              {TAG_LABELS[tag]}
            </Pill>
            {formattedDate && (
              <span className="shrink-0 text-s text-secondary-40">
                {formattedDate}
              </span>
            )}
          </div>

          {/* Heading with superscript unread dot */}
          <div className="flex items-start gap-2xs">
            <h3 className="m-0 text-l font-bold text-primary-10 min-l:text-xl">
              {heading}
            </h3>
            {!isRead && (
              <span
                data-testid="whatsNewUnreadDot"
                aria-label="Unread"
                className="mt-2xs h-[6px] w-[6px] shrink-0 rounded-full bg-core-40"
              />
            )}
          </div>

          {subHeading && (
            <p className="m-0 text-s font-bold text-black min-l:text-m">
              {subHeading}
            </p>
          )}
          {body && <p className="m-0 text-s text-black min-l:text-m">{body}</p>}

          {safeHref && (
            <a
              href={safeHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onLearnMore}
              className={clsx(
                'flex w-max items-center gap-xs pt-xs text-s font-bold no-underline min-l:text-m',
                isRead ? 'text-primary-20' : 'text-primary-40',
              )}
            >
              {linkText || 'Learn more'}
              <IconAngleRight className="h-[16px] w-[16px] min-l:h-[20px] min-l:w-[20px]" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
